/*
 * R-8 Look detail controller — Phase A1. Route A (approved 2026-10-22): /pages/look?id=<id>. Reads and writes only through
 * NcApp.gateway() (mock in A1): getAuthState, getLook, getProfile (owner check), listComments, addComment, setLookLike,
 * setLookSaved, setFavorite (garments), duplicateLook, deleteLook. No network, no persistence, no images of its own: an image
 * appears only when gateway.getAsset(assetId) returns a `src`; the mock returns none, so the neutral media slot shows.
 *
 * Adapter (GATEWAY GAPS, see the report): the mock's look has no owner id, visibility, description, date, location, image list,
 * saves count, garment availability or author name/avatar. normalizeLook() reads them when present (ownerId, visibility,
 * description, createdAt, location, images, saves, commentCount, authorName, authorAvatarAssetId, products[].available) and
 * falls back to what exists: owner = the signed-in profile's handle equals the look's author handle; visibility = public.
 *
 * Permissions (approved by Javi, chat 2026-10-22), role = visitor | user | owner (permissionsFor):
 *   visitor: public look, Compartir; Seguir / Me gusta / Guardar / Comentar fire `nc-look-detail:signin` (no routing).
 *   user (someone else's look): Seguir, Me gusta, Guardar, Comentar, Compartir, Reportar. No Editar / Duplicar / visibility.
 *   owner: Editar, Duplicar, visibility (only "Público" in A1), Eliminar (explicit confirmation); sees the own private look.
 *   Private or deleted look of someone else, or an unknown id: "not available", exactly like a nonexistent look.
 * DOCUMENTED DECISION (round 2): the approved table does not list Me gusta / Guardados for the author of the look. The author keeps
 * seeing both figures (the reference image shows them) as READ-ONLY numbers: aria-disabled="true", no tab stop, no toggle, no
 * event. Everything else not allowed is hidden, never disabled.
 * Round 2 additions (reference image E-1): fullscreen button on the gallery stage (Fullscreen API on [data-look-main]; hidden when the
 * browser has no API; Esc exits and focus returns to the button); "Ver todas" in the garments header (garments beyond the limit
 * start hidden, the link reveals them and disappears; limit = section settings); "+" tag chip for the author only, firing
 * `nc-look-detail:action` {action:'add-tag'} like "Editar" (tag editing is Phase B).
 * Seguir and Reportar only fire events (follow model and moderation flow are UNKNOWN). "Probar look", "Compartir", "Editar",
 * "Añadir prendas" and every navigation fire `nc-look-detail:action` {action, lookId} (Phase B wires them). Comments live inline
 * from 600 px and in a bottom sheet below 600 px (PROPOSED).
 * Pure helpers are exported for node:test.
 */
(function () {
  function parseLookId(search) {
    try {
      var id = new URLSearchParams(search || '').get('id');
      return id && id.trim() ? id.trim() : '';
    } catch (e) {
      return '';
    }
  }

  function toImage(entry) {
    if (!entry) return null;
    if (typeof entry === 'string') return { assetId: entry };
    return { assetId: entry.assetId || entry.id || null, alt: entry.alt || '' };
  }

  // gateway look -> view model; every field the mock lacks has a named fallback
  function normalizeLook(raw) {
    raw = raw || {};
    var author = raw.author;
    var handle = typeof author === 'string' ? author : (author && author.handle) || '';
    var name = raw.authorName || (author && typeof author === 'object' && author.displayName) || '';
    var list = Array.isArray(raw.images) && raw.images.length ? raw.images : raw.assetId ? [raw.assetId] : [];
    var visibility = raw.deleted === true ? 'deleted' : raw.visibility || 'public';
    return {
      id: raw.id,
      title: raw.title || '',
      description: raw.description || '',
      tags: Array.isArray(raw.tags) ? raw.tags.slice() : [],
      authorHandle: handle,
      authorName: name,
      authorAvatarAssetId: raw.authorAvatarAssetId || null,
      ownerId: raw.ownerId || null,
      visibility: visibility,
      createdAt: raw.createdAt || '',
      location: raw.location || '',
      images: list.map(toImage).filter(Boolean),
      likes: typeof raw.likes === 'number' ? raw.likes : 0,
      liked: !!raw.liked,
      saved: !!raw.saved,
      saves: typeof raw.saves === 'number' ? raw.saves : null,
      commentCount: typeof raw.commentCount === 'number' ? raw.commentCount : null,
      products: (raw.products || []).map(function (p) {
        return { id: p.id, brand: p.brand || '', title: p.title || '', price: p.price || '', colors: (p.colors || []).slice(), favorite: !!p.favorite, assetId: p.assetId || null, soldOut: p.soldOut === true || p.available === false };
      })
    };
  }

  // visitor (no session) | owner | user (someone else's look)
  function resolveRole(look, auth, profile) {
    if (!auth || auth.status !== 'authenticated') return 'visitor';
    if (look.ownerId && auth.userId) return look.ownerId === auth.userId ? 'owner' : 'user';
    if (profile && profile.handle && look.authorHandle) return profile.handle === look.authorHandle ? 'owner' : 'user';
    return 'user';
  }

  // private or deleted looks are for their author only (a deleted look is for nobody); never reveal that they exist
  function canView(look, role) {
    if (!look) return false;
    if (look.visibility === 'deleted') return false;
    if (look.visibility === 'private') return role === 'owner';
    return true;
  }

  // approved permission table: what is shown (hidden otherwise, never disabled) and whether the session gate applies
  function permissionsFor(role) {
    var owner = role === 'owner';
    var visitor = role === 'visitor';
    return {
      gated: visitor,
      follow: !owner,
      like: !owner,
      save: !owner,
      comment: true,
      share: true,
      report: role === 'user',
      edit: owner,
      duplicate: owner,
      visibility: owner,
      remove: owner,
      addGarments: owner
    };
  }

  // a missing look (validation / 404 / 403) never says why; transport failures are recoverable errors
  function classifyError(err) {
    var kind = err && err.kind;
    if (kind === 'validation' || kind === 'forbidden' || kind === 'not_found' || kind === 'notfound' || (err && err.status === 404)) return 'notfound';
    return 'error';
  }

  function formatCount(n) {
    n = Math.max(0, parseInt(n, 10) || 0);
    if (n < 1000) return String(n);
    var k = (n / 1000).toFixed(1).replace(/\.0$/, '').replace('.', ',');
    return k + 'K';
  }

  // "Hace 2 días" relative to the gateway clock; absolute date past 30 days
  function formatRelative(iso, nowIso) {
    var t = Date.parse(iso);
    var now = Date.parse(nowIso);
    if (isNaN(t) || isNaN(now)) return '';
    var s = Math.max(0, Math.round((now - t) / 1000));
    var out;
    if (s < 60) out = 'hace un momento';
    else if (s < 3600) out = 'hace ' + Math.floor(s / 60) + ' min';
    else if (s < 86400) out = 'hace ' + Math.floor(s / 3600) + ' h';
    else if (s < 86400 * 30) { var d = Math.floor(s / 86400); out = 'hace ' + d + (d === 1 ? ' día' : ' días'); }
    else return new Date(t).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
    return out.charAt(0).toUpperCase() + out.slice(1);
  }

  function initialOf(text) {
    var t = String(text || '').replace(/^@/, '').trim();
    return t ? t.charAt(0).toUpperCase() : '?';
  }

  // thumbnails shown before "+N": a column of 5 only from 1100 px (the column of 5 plus "+N" does not fit the gallery height at 834), 4 below (QA look round 1)
  function visibleThumbs(wide) { return wide ? 5 : 4; }

  // product page of a garment through the route map (NcNav, /products/<handle>); '' when there is none
  function garmentHref(id) {
    try { return (typeof window !== 'undefined' && window.NcNav && window.NcNav.url && window.NcNav.url('product', { product: id })) || ''; } catch (e) { return ''; }
  }
  // garments shown before "Ver todas": mobile limit below 834 px, wide limit from 834 px; 0 or NaN setting = no limit
  function garmentLimit(wide, mobileLimit, wideLimit) {
    var n = parseInt(wide ? wideLimit : mobileLimit, 10);
    return n > 0 ? n : Infinity;
  }

  function fill(template, n) { return String(template || '').replace('{n}', String(n)); }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { parseLookId: parseLookId, normalizeLook: normalizeLook, resolveRole: resolveRole, canView: canView, permissionsFor: permissionsFor, classifyError: classifyError, formatCount: formatCount, formatRelative: formatRelative, initialOf: initialOf, visibleThumbs: visibleThumbs, garmentLimit: garmentLimit, garmentHref: garmentHref, fill: fill };
  }
  if (typeof customElements === 'undefined' || customElements.get('nc-look-detail')) return;

  var SVG_IMG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><rect width="18" height="18" x="3" y="3" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21"/></svg>';

  class NcLookDetail extends HTMLElement {
    connectedCallback() {
      this.gw = window.NcApp.gateway();
      this.lookId = parseLookId(window.location.search);
      this.req = 0;
      this.look = null;
      this.role = 'visitor';
      this.perms = permissionsFor('visitor');
      this.texts = {
        signIn: (this.dataset.msgSignIn || '').split('|'),
        feedback: (this.dataset.msgFeedback || '').split('|'),
        gallery: (this.dataset.msgGallery || '').split('|')
      };
      this.main = this.querySelector('.nc-look__main');
      this.statusEl = this.querySelector('[data-status]');
      this.noteEl = this.querySelector('[data-note]');
      this.mqWide = window.matchMedia('(min-width: 834px)');
      this.mqMobile = window.matchMedia('(max-width: 599px)');
      this.mqThumbs = window.matchMedia('(min-width: 1100px)');
      this.commentState = { page: 0, total: 0, loaded: 0, pageSize: 4, seq: 0 };
      this.expanded = false;
      this.garmentsExpanded = false;
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('submit', this.onSubmit.bind(this));
      this.addEventListener('nc-card:toggle', this.onGarmentToggle.bind(this));
      this.addEventListener('nc-menu:action', this.onMenuAction.bind(this));
      this.addEventListener('nc-sheet:action', this.onSheetAction.bind(this));
      this.addEventListener('nc-gallery:change', this.onGalleryChange.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this), true);
      var self = this;
      var onMq = function () { self.applyCollapse(); self.applyGarmentLimit(false); self.placeComments(); };
      if (this.mqWide.addEventListener) { this.mqWide.addEventListener('change', onMq); this.mqMobile.addEventListener('change', onMq); this.mqThumbs.addEventListener('change', onMq); }
      this.onFullscreenChange = this.syncFullscreen.bind(this);
      document.addEventListener('fullscreenchange', this.onFullscreenChange);
      document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);
      this.load();
    }

    disconnectedCallback() {
      document.removeEventListener('fullscreenchange', this.onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);
    }

    q(sel) { return this.querySelector(sel); }
    qa(sel) { return Array.prototype.slice.call(this.querySelectorAll(sel)); }

    emit(name, detail) {
      this.dispatchEvent(new CustomEvent('nc-look-detail:' + name, { bubbles: true, detail: Object.assign({ lookId: this.lookId }, detail || {}) }));
    }

    announce(text) { if (this.statusEl) this.statusEl.textContent = text; }

    toast(tone, message) { if (window.NcToast) window.NcToast.show({ tone: tone, message: message }); }

    // ---- states ----
    show(name) {
      ['skeleton', 'notfound', 'error', 'content'].forEach(function (n) {
        var el = this.q('[data-block="' + n + '"]');
        if (el) el.hidden = n !== name;
      }, this);
      var active = document.activeElement;
      if (!active || active === document.body || active.closest('[hidden]')) {
        this.main.focus({ preventScroll: true });
      }
    }

    load() {
      var self = this;
      var seq = ++this.req;
      this.show('skeleton');
      if (!this.lookId) { this.show('notfound'); return; }
      Promise.all([
        this.gw.getAuthState().catch(function () { return { status: 'anonymous' }; }),
        this.gw.getLook(this.lookId)
      ]).then(function (res) {
        var look = normalizeLook(res[1]);
        var auth = res[0];
        var needProfile = auth && auth.status === 'authenticated' && !look.ownerId;
        return (needProfile ? self.gw.getProfile().catch(function () { return null; }) : Promise.resolve(null)).then(function (profile) {
          if (seq !== self.req) return;
          self.auth = auth;
          self.look = look;
          self.role = resolveRole(look, auth, profile);
          self.perms = permissionsFor(self.role);
          if (!canView(look, self.role)) { self.show('notfound'); return; }
          self.render();
          self.show('content');
          self.loadComments(true);
        });
      }, function (err) {
        if (seq !== self.req) return;
        self.show(classifyError(err));
      });
    }

    // ---- rendering ----
    resolveAsset(assetId) {
      if (!assetId) return Promise.resolve('');
      return this.gw.getAsset(assetId).then(function (a) { return (a && a.src) || ''; }, function () { return ''; });
    }

    // the only selectable visibility in A1 is public; an own PRIVATE look shows its real state (read-only text), never "Público"
    showVisibility(look) {
      var root = this.q('[data-perm="visibility"]');
      if (!root) return;
      var value = root.querySelector('[data-nc-select-value]');
      var input = root.querySelector('input[name="visibility"]');
      if (!this.visibilityPublic && value) this.visibilityPublic = value.textContent;
      var isPrivate = look.visibility === 'private';
      if (value) value.textContent = isPrivate ? (this.dataset.visibilityPrivate || 'Privado') : this.visibilityPublic;
      if (input) input.value = isPrivate ? 'private' : 'public';
    }

    render() {
      var look = this.look;
      var perms = this.perms;
      // an own look goes back to «Mis looks» of the profile, not to the community (QA 2026-11-01: the person lost where they were)
      var back = this.q('[data-back]');
      if (back && this.role === 'owner') {
        var own = (window.NcNav && window.NcNav.url && window.NcNav.url('profile')) || '/pages/profile';
        back.setAttribute('href', own.split('#')[0] + '#profile-looks');
        var label = back.querySelector('span');
        if (label) label.textContent = this.dataset.msgBackOwn || 'Mis looks';
      }
      // permissions: hidden, never disabled
      var map = { follow: perms.follow, edit: perms.edit, duplicate: perms.duplicate, visibility: perms.visibility, like: true, save: true, share: perms.share,
        'menu-owner': perms.remove, 'menu-user': perms.report && !perms.remove, 'comment-form': !perms.gated, 'comment-signin': perms.gated };
      this.qa('[data-perm]').forEach(function (el) { var k = el.getAttribute('data-perm'); el.hidden = !map[k]; });
      this.showVisibility(look);
      this.q('[data-title]').textContent = look.title;
      var desc = this.q('[data-description]');
      desc.textContent = look.description;
      desc.hidden = !look.description;
      var tags = this.q('[data-tags]');
      tags.textContent = '';
      look.tags.forEach(function (t) { var li = document.createElement('li'); li.className = 'nc-look__tag'; li.textContent = t; tags.appendChild(li); });
      if (perms.edit) {
        // author only: "+" chip after the tags; it is a button that only fires an event (tag editing is Phase B)
        var add = document.createElement('li');
        add.className = 'nc-look__tag-add';
        var addBtn = document.createElement('button');
        addBtn.type = 'button';
        addBtn.className = 'nc-look__tag-add-btn';
        addBtn.setAttribute('data-add-tag', ''); addBtn.setAttribute('data-nc-community-area', '');
        addBtn.setAttribute('aria-label', this.dataset.msgAddTag || 'Añadir etiqueta');
        addBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M5 12h14M12 5v14"/></svg>';
        add.appendChild(addBtn);
        tags.appendChild(add);
      }
      tags.hidden = !look.tags.length && !perms.edit;
      // author
      this.q('[data-author-name]').textContent = look.authorName || look.authorHandle;
      var handle = this.q('[data-author-handle]');
      handle.textContent = look.authorName ? look.authorHandle : '';
      handle.hidden = !look.authorName;
      var meta = [];
      var rel = look.createdAt ? formatRelative(look.createdAt, this.gw.now ? this.gw.now() : look.createdAt) : '';
      if (rel) meta.push(rel);
      if (look.location) meta.push(look.location);
      var metaEl = this.q('[data-author-meta]');
      metaEl.textContent = meta.join(' · ');
      metaEl.hidden = !meta.length;
      this.fillAvatar(this.q('[data-avatar]'), look.authorName || look.authorHandle, look.authorAvatarAssetId);
      // metrics
      this.setMetric('like', look.likes, look.liked);
      this.setMetric('save', look.saves, look.saved);
      this.setMetric('comments', look.commentCount, null);
      // the author sees the counts of the own look as read-only figures (PROPOSED), not toggles
      this.qa('[data-metric="like"], [data-metric="save"]').forEach(function (b) {
        if (perms.like) { b.removeAttribute('data-readonly'); b.removeAttribute('aria-disabled'); b.removeAttribute('tabindex'); return; }
        b.setAttribute('data-readonly', '');
        b.removeAttribute('aria-pressed');
        b.setAttribute('aria-disabled', 'true');
        b.setAttribute('tabindex', '-1');
      });
      this.renderGallery();
      this.renderGarments();
      this.setCommentsTitle(null);
      this.placeComments();
    }

    fillAvatar(el, label, assetId) {
      el.textContent = initialOf(label);
      if (!assetId) return;
      this.resolveAsset(assetId).then(function (src) {
        if (!src) return;
        var img = document.createElement('img');
        img.src = src; img.alt = ''; img.width = 44; img.height = 44;
        el.textContent = '';
        el.appendChild(img);
      });
    }

    setMetric(kind, count, pressed) {
      var btn = this.q('[data-metric="' + kind + '"]');
      if (!btn) return;
      var out = btn.querySelector('[data-count]');
      out.textContent = typeof count === 'number' ? formatCount(count) : '';
      btn.setAttribute('data-value', typeof count === 'number' ? String(count) : '');
      if (pressed !== null && btn.hasAttribute('aria-pressed')) btn.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    }

    // ---- gallery (markup of nc-gallery; thumbnails beyond the visible count collapse into "+N") ----
    renderGallery() {
      var self = this;
      var images = this.look.images;
      var gallery = this.q('nc-gallery');
      var main = this.q('[data-look-main]');
      var ul = this.q('[data-thumbs]');
      ul.textContent = '';
      var oldImg = main.querySelector('img');
      if (oldImg) oldImg.remove();
      var label = this.texts.gallery[1] || 'Imagen';
      images.forEach(function (im, i) {
        var li = document.createElement('li');
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'nc-gallery__thumb';
        b.setAttribute('data-index', String(i + 1));
        b.setAttribute('data-src', '');
        b.setAttribute('data-alt', im.alt || '');
        b.setAttribute('aria-label', label + ' ' + (i + 1) + ' de ' + images.length);
        b.setAttribute('tabindex', i === 0 ? '0' : '-1');
        if (i === 0) b.setAttribute('aria-current', 'true');
        b.innerHTML = '<span class="nc-look__thumb-ph">' + SVG_IMG + '</span>';
        li.appendChild(b);
        ul.appendChild(li);
        self.resolveAsset(im.assetId).then(function (src) {
          if (!src) return;
          b.setAttribute('data-src', src);
          var t = document.createElement('img');
          t.src = src; t.alt = ''; t.width = 120; t.height = 150; t.loading = 'lazy';
          b.textContent = '';
          b.appendChild(t);
          if (i === 0 && !main.querySelector('img')) {
            var big = document.createElement('img');
            big.src = src; big.alt = im.alt || ''; big.width = 800; big.height = 1000;
            big.setAttribute('sizes', '(min-width: 990px) 50vw, 100vw');
            main.insertBefore(big, main.firstChild);
          }
        });
      });
      this.expanded = false;
      var multi = images.length > 1;
      ul.hidden = !multi;
      gallery.toggleAttribute('data-single', !multi);
      this.qa('[data-arrow-wrap]').forEach(function (a) { a.hidden = !multi; });
      this.q('[data-counter]').hidden = !multi;
      this.updateCounter(1);
      this.applyCollapse();
      this.wireLens();
      var fs = this.q('[data-fullscreen]');
      fs.hidden = !(images.length && (this.fullscreenSupported() || typeof HTMLDialogElement === 'function'));
      this.syncFullscreen();
    }

    // ---- zoom viewer (same as the product page: assets/nc-image-viewer.js) ----
    viewerSources() {
      var out = [];
      this.qa('.nc-gallery__thumb').forEach(function (b) { var u = b.getAttribute('data-src'); if (u) out.push(u); });
      return out;
    }

    openViewer(trigger) {
      var srcs = this.viewerSources();
      var dlg = this.q('[data-look-viewer]');
      if (!srcs.length || !dlg || !window.NcImageViewer) return false;
      var texts = String(this.dataset.msgViewer || '').split('|');
      var pos = texts[3] || 'Imagen {n} de {t}';
      var title = this.look && this.look.title || '';
      var cur = this.qa('.nc-gallery__thumb').filter(function (b) { return b.getAttribute('aria-current') === 'true'; })[0];
      var at = cur ? Math.max(0, parseInt(cur.getAttribute('data-index'), 10) - 1) : 0;
      return window.NcImageViewer.open(dlg, {
        srcs: srcs, index: at, trigger: trigger || null,
        alt: function (i) { return title + ' ' + (i + 1); },
        pos: function (i, n) { return pos.replace('{n}', String(i + 1)).replace('{t}', String(n)); }
      });
    }

    // desktop: the main photo follows the cursor as a magnifier (1.8x), only with a fine pointer that hovers (as on the product page)
    wireLens() {
      if (this.lensWired || !window.matchMedia || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      this.lensWired = true;
      var self = this;
      var main = this.q('[data-look-main]');
      main.classList.add('nc-lens-photo');
      main.addEventListener('pointermove', function (e) {
        var img = main.querySelector('img');
        if (!img || e.target.closest('button')) { main.removeAttribute('data-lens'); return; }
        var r = main.getBoundingClientRect();
        img.style.transformOrigin = ((e.clientX - r.left) / r.width * 100).toFixed(1) + '% ' + ((e.clientY - r.top) / r.height * 100).toFixed(1) + '%';
        main.setAttribute('data-lens', '');
      });
      main.addEventListener('pointerleave', function () { main.removeAttribute('data-lens'); });
    }

    // ---- fullscreen (Fullscreen API on the gallery stage; no API = no button) ----
    fullscreenSupported() {
      var main = this.q('[data-look-main]');
      return !!(main && (main.requestFullscreen || main.webkitRequestFullscreen) && (document.fullscreenEnabled || document.webkitFullscreenEnabled));
    }

    fullscreenElement() { return document.fullscreenElement || document.webkitFullscreenElement || null; }

    toggleFullscreen() {
      var main = this.q('[data-look-main]');
      var btn = this.q('[data-fullscreen]');
      if (this.fullscreenElement() === main) {
        var out = document.exitFullscreen ? document.exitFullscreen() : document.webkitExitFullscreen();
        if (out && out.catch) out.catch(function () {});
        return;
      }
      try {
        var req = main.requestFullscreen ? main.requestFullscreen() : main.webkitRequestFullscreen();
        if (req && req.catch) req.catch(function () { btn.hidden = true; });
      } catch (e) { btn.hidden = true; }
    }

    // keeps label and icon in step with the real state (also when the browser leaves fullscreen with Esc); focus goes back to the button
    syncFullscreen() {
      var btn = this.q('[data-fullscreen]');
      if (!btn) return;
      var on = this.fullscreenElement() === this.q('[data-look-main]');
      var was = btn.getAttribute('data-on') === 'true';
      btn.setAttribute('data-on', on ? 'true' : 'false');
      btn.setAttribute('aria-label', (on ? btn.getAttribute('data-label-exit') : btn.getAttribute('data-label-enter')) || '');
      if (was && !on && !btn.hidden) btn.focus({ preventScroll: true });
    }

    updateCounter(index) {
      this.q('[data-counter]').textContent = index + '/' + this.look.images.length;
    }

    applyCollapse() {
      if (!this.look) return;
      var ul = this.q('[data-thumbs]');
      var more = ul.querySelector('[data-more-li]');
      if (more) more.remove();
      var items = Array.prototype.slice.call(ul.children);
      var limit = visibleThumbs(this.mqThumbs.matches);
      var hiddenCount = this.expanded ? 0 : Math.max(0, items.length - limit);
      items.forEach(function (li, i) { li.hidden = i >= limit && !this.expanded; }, this);
      if (hiddenCount > 0) {
        var li = document.createElement('li');
        li.setAttribute('data-more-li', '');
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'nc-look__more-thumb';
        b.setAttribute('data-more', '');
        b.setAttribute('aria-label', fill(this.texts.gallery[4] || 'Ver {n} más', hiddenCount));
        b.textContent = '+' + hiddenCount;
        li.appendChild(b);
        ul.appendChild(li);
      }
    }

    expand() {
      if (this.expanded) return;
      this.expanded = true;
      this.applyCollapse();
    }

    goto(offset) {
      var gallery = this.q('nc-gallery');
      var thumbs = this.qa('.nc-gallery__thumb');
      if (!thumbs.length) return;
      var cur = thumbs.findIndex(function (t) { return t.getAttribute('aria-current') === 'true'; });
      var next = (cur + offset + thumbs.length) % thumbs.length;
      if (next >= visibleThumbs(this.mqThumbs.matches)) this.expand();
      gallery.select(next, false);
    }

    onGalleryChange(event) { this.updateCounter(event.detail.index); }

    // moving with the arrow keys past the last visible thumbnail first reveals the rest
    onKeydown(event) {
      if (!event.target.closest || !event.target.closest('.nc-gallery__thumb')) return;
      if (['ArrowRight', 'ArrowDown', 'End', 'ArrowLeft', 'ArrowUp', 'Home'].indexOf(event.key) >= 0) this.expand();
    }

    // ---- garments ----
    renderGarments() {
      var self = this;
      var list = this.look.products;
      var grid = this.q('[data-garments]');
      grid.textContent = '';
      this.q('[data-garments-title]').textContent = fill(this.dataset.msgGarmentsTitle, list.length);
      this.garmentsExpanded = false;
      this.q('[data-view-all]').hidden = true;
      var ownerEmpty = this.q('[data-garments-empty="owner"]');
      var otherEmpty = this.q('[data-garments-empty="other"]');
      ownerEmpty.hidden = true;
      otherEmpty.hidden = true;
      grid.hidden = !list.length;
      if (!list.length) {
        (this.perms.addGarments ? ownerEmpty : otherEmpty).hidden = false;
        return;
      }
      var tpl = this.q('template[data-tpl="garment"]');
      list.forEach(function (p, i) {
        var card = tpl.content.querySelector('article').cloneNode(true);
        card.setAttribute('data-product-id', p.id);
        card.querySelector('.nc-product-card__brand').textContent = p.brand;
        // analytics: the card carries its brand slug so item_impression has a brand (a card without it biases M04 and M12)
        if ((p.brandInfo && p.brandInfo.slug) || p.brandSlug) card.setAttribute('data-nc-brand', (p.brandInfo && p.brandInfo.slug) || p.brandSlug);
        var link = card.querySelector('.nc-card__link');
        link.textContent = p.title;
        link.setAttribute('data-garment', p.id);
        card.setAttribute('data-entity-id', p.id); // nc-nav linkCards fills the href once the route map is ready if it was not at render time
        // UX look/ficha (1-11): a real link to the product page (/products/<handle>) so it opens in a new tab, is announced as a link and works by keyboard
        var href = garmentHref(p.id);
        if (href) link.setAttribute('href', href);
        card.querySelector('.nc-product-card__price').textContent = p.price;
        var swatches = card.querySelector('.nc-product-card__swatches');
        if (swatches) {
          swatches.textContent = '';
          p.colors.slice(0, 3).forEach(function (c) { var li = document.createElement('li'); li.className = 'nc-product-card__swatch'; li.style.background = c; swatches.appendChild(li); });
          if (!p.colors.length) swatches.remove();
        }
        var fav = card.querySelector('.nc-toggle');
        fav.setAttribute('data-id', p.id);
        fav.setAttribute('aria-label', (self.dataset.msgGarmentFav || 'Guardar en favoritos') + ': ' + p.title);
        fav.setAttribute('aria-pressed', p.favorite ? 'true' : 'false');
        if (p.soldOut) {
          card.setAttribute('data-sold-out', '');
          var badge = document.createElement('span');
          badge.className = 'nc-look__soldout';
          badge.textContent = self.dataset.msgSoldOut || 'Agotado';
          card.querySelector('.nc-card__media').appendChild(badge);
        }
        self.resolveAsset(p.assetId).then(function (src) {
          if (!src) return;
          var img = document.createElement('img');
          img.src = src; img.alt = p.brand + ' ' + p.title; img.width = 242; img.height = 190;
          img.loading = i < 4 ? 'eager' : 'lazy';
          card.querySelector('.nc-card__media').insertBefore(img, card.querySelector('.nc-card__media').firstChild);
        });
        grid.appendChild(card);
      });
      this.renderStrip(list);
      this.refreshHrefs();
      this.applyGarmentLimit(false);
    }

    // the route map (NcNav) can load after the first render: fill the garment hrefs again when the page has loaded
    refreshHrefs() {
      var self = this;
      var fill = function () {
        self.qa('a[data-garment]').forEach(function (a) {
          var h = a.getAttribute('href');
          if (h && h !== '#') return;
          var u = garmentHref(a.getAttribute('data-garment'));
          if (u) a.setAttribute('href', u);
        });
      };
      fill();
      if (document.readyState !== 'complete' && !this.hrefsPending) { this.hrefsPending = true; window.addEventListener('load', function () { self.hrefsPending = false; fill(); setTimeout(fill, 400); }, { once: true }); }
      else setTimeout(fill, 400);
    }

    // compact strip of garment thumbnails under the title (phones and tablets, CSS hides it from 834 px): what the look wears before the first scroll
    renderStrip(list) {
      var self = this;
      var strip = this.q('[data-strip]');
      if (!strip) return;
      strip.textContent = '';
      strip.hidden = !list.length;
      if (!list.length) return;
      strip.setAttribute('aria-label', fill(this.dataset.msgGarmentsTitle, list.length));
      list.forEach(function (p) {
        var li = document.createElement('li');
        var a = document.createElement('a');
        a.className = 'nc-look__strip-link nc-focusable';
        a.setAttribute('aria-label', p.title);
        var href = garmentHref(p.id);
        a.setAttribute('href', href || '#');
        a.setAttribute('data-garment', p.id);
        a.setAttribute('data-from-strip', '');
        self.resolveAsset(p.assetId).then(function (src) {
          if (!src) return;
          var img = document.createElement('img');
          img.src = src; img.alt = ''; img.width = 56; img.height = 56; img.loading = 'lazy';
          a.appendChild(img);
        });
        li.appendChild(a);
        strip.appendChild(li);
      });
    }

    // cards beyond the limit start hidden; "Ver todas" shows when something is hidden and disappears once everything is shown
    applyGarmentLimit(focusFirstRevealed) {
      if (!this.look) return;
      var cards = Array.prototype.slice.call(this.q('[data-garments]').children);
      var limit = this.garmentsExpanded ? Infinity : garmentLimit(this.mqWide.matches, this.dataset.garmentsLimit, this.dataset.garmentsLimitWide);
      var firstRevealed = null;
      cards.forEach(function (c, i) {
        var hide = i >= limit;
        if (c.hidden && !hide && !firstRevealed) firstRevealed = c;
        c.hidden = hide;
      });
      var link = this.q('[data-view-all]');
      var hidden = cards.length > limit;
      var hadFocus = document.activeElement === link;
      link.hidden = !hidden;
      link.setAttribute('aria-expanded', 'false');
      if (focusFirstRevealed && firstRevealed) {
        var target = firstRevealed.querySelector('.nc-card__link');
        if (target) target.focus();
      } else if (hadFocus && !hidden) {
        this.q('[data-garments-title]').setAttribute('tabindex', '-1');
        this.q('[data-garments-title]').focus();
      }
    }

    expandGarments() {
      var link = this.q('[data-view-all]');
      link.setAttribute('aria-expanded', 'true');
      this.garmentsExpanded = true;
      this.applyGarmentLimit(true);
    }

    onGarmentToggle(event) {
      var d = event.detail;
      if (d.kind !== 'favorite') return;
      if (this.perms.gated) {
        event.preventDefault();
        this.gate('garment');
        return;
      }
      var btn = event.target.closest('.nc-toggle');
      var self = this;
      window.NcCard.setState(btn, 'loading');
      this.gw.setFavorite({ entity: 'product', id: d.id, value: d.pressed }).then(function () {
        window.NcCard.setState(btn, 'default');
      }, function (err) {
        window.NcCard.setState(btn, 'error', self.texts.feedback[7]);
        if (err && err.kind === 'unauthenticated') self.gate('garment');
      });
    }

    // ---- session gate and pending notes ----
    gate(reason) {
      var idx = { follow: 0, like: 1, save: 2, comment: 3, garment: 4 }[reason];
      this.showNote(this.texts.signIn[idx] || '');
      this.emit('signin', { reason: reason });
    }

    showNote(text) {
      this.noteEl.textContent = text;
      this.noteEl.hidden = !text;
    }

    // ---- clicks ----
    onClick(event) {
      var t = event.target;
      var retry = t.closest('[data-block="error"] [data-nc-retry]');
      if (retry) { this.load(); return; }
      if (t.closest('[data-retry-comments]')) { this.loadComments(true); return; }
      if (t.closest('[data-view-all]')) { this.expandGarments(); return; }
      if (t.closest('[data-fullscreen]')) { if (!this.openViewer(t.closest('[data-fullscreen]'))) this.toggleFullscreen(); return; }
      // a tap on the photo opens the zoom viewer (the usual gesture; on desktop the magnifier says zoom-in)
      if (t.closest('[data-look-main]') && !t.closest('button, a, .nc-card__media-error') && t.closest('[data-look-main]').querySelector('img')) { if (this.openViewer(t.closest('[data-look-main]'))) return; }
      if (t.closest('[data-add-tag]')) { this.showNote(this.dataset.msgPending); this.emit('action', { action: 'add-tag' }); return; }
      var arrow = t.closest('.nc-icon-button[data-id="prev"], .nc-icon-button[data-id="next"]');
      if (arrow) { this.goto(arrow.getAttribute('data-id') === 'next' ? 1 : -1); return; }
      if (t.closest('[data-more]')) { this.expand(); var first = this.qa('.nc-gallery__thumb').filter(function (b) { return !b.closest('[hidden]'); }); var cur = first.filter(function (b) { return b.getAttribute('aria-current') === 'true'; })[0] || first[0]; if (cur) cur.focus(); return; }
      var metric = t.closest('[data-metric]');
      if (metric) { this.onMetric(metric); return; }
      var garment = t.closest('[data-garment]');
      var viaMedia = false;
      if (!garment && !t.closest('.nc-toggle, .nc-card__retry, button, a')) {
        // the photo of a garment card opens the same product page as its title
        var mediaCard = t.closest('.nc-product-card');
        if (mediaCard && t.closest('.nc-card__media')) { garment = mediaCard.querySelector('[data-garment]'); viaMedia = !!garment; }
      }
      if (garment) {
        var gid = garment.getAttribute('data-garment');
        var real = garment.getAttribute('href');
        if (real && real !== '#' && real.charAt(0) !== '#') {
          // nc-nav remembers the look for the product page's "Volver al look" and, for the photo, does the navigation
          this.emit('action', { action: 'open-garment', productId: gid, href: real, lookTitle: this.look && this.look.title || '', viaMedia: viaMedia });
          // the title/strip anchors navigate by themselves (new tab, middle click and keyboard keep working)
          return;
        }
        event.preventDefault(); this.emit('action', { action: 'open-garment', productId: gid, lookTitle: this.look && this.look.title || '' }); return;
      }
      if (t.closest('[data-block="content"] [data-cta] .nc-button')) { this.emit('action', { action: 'try' }); this.tryLook(); return; }
      var author = t.closest('.nc-look__author-actions');
      var button = t.closest('.nc-button');
      if (author && button) {
        var host = button.closest('[data-perm]');
        var k = host && host.getAttribute('data-perm');
        if (k === 'follow') { if (this.perms.gated) this.gate('follow'); else { this.showNote(this.dataset.msgPending); this.emit('action', { action: 'follow' }); } }
        else if (k === 'edit') { this.showNote(this.dataset.msgPending); this.emit('action', { action: 'edit' }); }
        else if (k === 'duplicate') this.duplicate(button);
        return;
      }
      var menuBtn = t.closest('.nc-icon-button[data-id="menu-owner"], .nc-icon-button[data-id="menu-user"]');
      if (menuBtn) {
        var id = menuBtn.getAttribute('data-id') === 'menu-owner' ? 'look-menu-owner' : 'look-menu-user';
        this.q('#' + id).toggle(menuBtn);
        return;
      }
      if (t.closest('[data-comments-open]')) { this.openCommentsSheet(t.closest('[data-comments-open]')); return; }
      var signin = t.closest('[data-perm="comment-signin"] .nc-button');
      if (signin) { this.gate('comment'); return; }
      if (t.closest('[data-garments-empty="owner"] [data-nc-retry]')) { this.showNote(this.dataset.msgPending); this.emit('action', { action: 'add-garments' }); return; }
      var more = t.closest('[data-comments-more] .nc-button');
      if (more) this.loadComments(false);
    }

    onMetric(btn) {
      var kind = btn.getAttribute('data-metric');
      if (kind === 'share') { this.emit('action', { action: 'share', url: window.location.href }); this.share(); return; }
      if (kind === 'comments') { this.onCommentsMetric(); return; }
      if (btn.hasAttribute('data-readonly')) return;
      if (this.perms.gated) { this.gate(kind); return; }
      if (btn.getAttribute('data-state') === 'loading') return;
      var self = this;
      var was = btn.getAttribute('aria-pressed') === 'true';
      var next = !was;
      var base = parseInt(btn.getAttribute('data-value'), 10);
      var hasCount = !isNaN(base);
      var apply = function (pressed, value) {
        btn.setAttribute('aria-pressed', pressed ? 'true' : 'false');
        if (hasCount) { btn.setAttribute('data-value', String(value)); btn.querySelector('[data-count]').textContent = formatCount(value); }
      };
      apply(next, hasCount ? base + (next ? 1 : -1) : 0);
      btn.setAttribute('data-state', 'loading');
      btn.setAttribute('aria-busy', 'true');
      var call = kind === 'like' ? this.gw.setLookLike({ lookId: this.lookId, value: next }) : this.gw.setLookSaved({ lookId: this.lookId, value: next });
      call.then(function () {
        btn.removeAttribute('data-state');
        btn.removeAttribute('aria-busy');
        self.announce(btn.querySelector('.nc-look__metric-label').textContent + (next ? ': sí' : ': no'));
      }, function (err) {
        apply(was, hasCount ? base : 0);
        btn.removeAttribute('aria-busy');
        btn.setAttribute('data-state', 'error');
        setTimeout(function () { if (btn.getAttribute('data-state') === 'error') btn.removeAttribute('data-state'); }, 2400);
        self.toast('error', self.texts.feedback[kind === 'like' ? 0 : 1]);
        if (err && err.kind === 'unauthenticated') self.gate(kind);
      });
    }

    // "Probar look" (UX batida UX-12): the look's garments go into the CURRENT look, the same as "Probarme el look" in
    // Comunidad (NcFittingRoom.addMany + notify). No navigation: the fitting room screen is reached from the menu.
    tryLook() {
      var fitting = window.NcFittingRoom;
      if (!fitting || !fitting.addMany || !this.look) return Promise.resolve(null);
      var copy = { added: this.dataset.msgTryAdded || 'Añadido a tu look actual', error: this.dataset.msgTryError || 'No se pudo añadir a tu look' };
      var ids = this.look.products.map(function (p) { return p.id; });
      return fitting.addMany(ids).then(function (r) { fitting.notify(r, copy); return r; }, function () { fitting.notify({ status: 'error' }, copy); return null; });
    }

    // "Compartir" (UX-13; R-8 route /pages/look?id is approved): the system share sheet when there is one, else copy the link
    share() {
      var self = this;
      var url = window.location.href;
      var copied = this.dataset.msgShareCopied || 'Enlace copiado';
      var failed = this.dataset.msgShareError || 'No se pudo copiar el enlace';
      if (navigator.share) {
        navigator.share({ title: document.title, url: url }).catch(function () { /* cancelled by the user: nothing to say */ });
        return;
      }
      var done = function () { self.toast('success', copied); };
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(done, function () { self.toast('error', failed); });
      else self.toast('error', failed);
    }

    onCommentsMetric() {
      if (this.mqMobile.matches) { this.openCommentsSheet(this.q('[data-metric="comments"]')); return; }
      var input = this.q('.nc-look__comments .nc-input__control');
      var target = input && !input.closest('[hidden]') ? input : this.q('[data-comments-title]');
      target.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      target.focus({ preventScroll: true });
    }

    // ---- menu, delete, duplicate ----
    onMenuAction(event) {
      var id = event.detail.id;
      if (id === 'report') {
        this.showNote(this.dataset.msgPending);
        this.emit('action', { action: 'report' });
      } else if (id === 'delete') {
        // the menu returns focus to its trigger once it has closed: open the dialog after that
        var sheet = this.q('#look-delete-sheet');
        var trigger = event.detail.trigger;
        // the destructive action must not take the initial focus: focus Cancel once the dialog is open
        setTimeout(function () {
          sheet.open(trigger);
          var cancel = sheet.querySelector('[data-action="cancel"]');
          if (cancel) cancel.focus();
        }, 0);
      }
    }

    onSheetAction(event) {
      if (event.target.closest('#look-delete-sheet') && event.detail.id === 'confirm') this.remove();
    }

    remove() {
      var self = this;
      this.gw.deleteLook(this.lookId).then(function () {
        self.toast('success', self.texts.feedback[5]);
        self.emit('deleted', {});
        self.look = null;
        self.req += 1;
        self.show('notfound');
        self.announce(self.q('[data-block="notfound"] .nc-state-panel__title').textContent);
      }, function () {
        self.toast('error', self.texts.feedback[6]);
      });
    }

    duplicate(button) {
      var self = this;
      if (button.getAttribute('data-state') === 'loading') return;
      button.setAttribute('data-state', 'loading');
      this.gw.duplicateLook(this.lookId).then(function (res) {
        button.removeAttribute('data-state');
        self.toast('success', self.texts.feedback[3]);
        self.emit('duplicated', { draftId: res && res.draftId });
      }, function () {
        button.removeAttribute('data-state');
        self.toast('error', self.texts.feedback[4]);
      });
    }

    // ---- comments ----
    setCommentsTitle(total) {
      var text = fill(this.dataset.msgCommentsTitle, total === null ? (this.look && this.look.commentCount !== null ? this.look.commentCount : '') : total);
      if (total === null && !(this.look && this.look.commentCount !== null)) text = text.replace(/\s*\(\)$/, '');
      this.q('[data-comments-title]').textContent = text;
      this.q('[data-sheet-title]').textContent = text;
      var open = this.q('[data-comments-open]');
      open.textContent = fill(this.dataset.msgCommentsOpen, total === null ? '' : total).replace(/\s*\(\)$/, '');
    }

    loadComments(reset) {
      var self = this;
      var st = this.commentState;
      var seq = ++st.seq;
      var list = this.q('[data-comment-list]');
      var skeleton = this.q('[data-comments-skeleton]');
      var more = this.q('[data-comments-more]');
      var moreBtn = more.querySelector('.nc-button');
      this.q('[data-comments-error]').hidden = true;
      if (reset) {
        st.page = 0; st.loaded = 0; st.total = 0;
        list.textContent = '';
        list.hidden = true;
        this.q('[data-comments-empty]').hidden = true;
        more.hidden = true;
        skeleton.hidden = false;
      } else if (moreBtn) {
        moreBtn.setAttribute('data-state', 'loading');
      }
      this.gw.listComments({ lookId: this.lookId, page: st.page + 1 }).then(function (res) {
        if (seq !== st.seq) return;
        skeleton.hidden = true;
        if (moreBtn) moreBtn.removeAttribute('data-state');
        st.page = res.page || st.page + 1;
        st.pageSize = res.pageSize || st.pageSize;
        st.total = res.total || 0;
        res.items.forEach(function (c) { list.appendChild(self.commentRow(c)); });
        st.loaded += res.items.length;
        self.afterComments();
      }, function () {
        if (seq !== st.seq) return;
        skeleton.hidden = true;
        if (moreBtn) moreBtn.removeAttribute('data-state');
        var err = self.q('[data-comments-error]');
        self.q('[data-comments-error-text]').textContent = self.dataset.msgCommentsError || '';
        err.hidden = false;
      });
    }

    afterComments() {
      var st = this.commentState;
      var list = this.q('[data-comment-list]');
      list.hidden = !list.children.length;
      var empty = this.q('[data-comments-empty]');
      empty.textContent = this.dataset.msgCommentsEmpty || '';
      empty.hidden = list.children.length > 0;
      this.q('[data-comments-more]').hidden = st.loaded >= st.total;
      this.setCommentsTitle(st.total);
      this.setMetric('comments', st.total, null);
    }

    commentRow(c) {
      var row = this.q('template[data-tpl="comment"]').content.querySelector('li').cloneNode(true);
      var name = (c.author && (c.author.displayName || c.author.handle)) || '';
      row.setAttribute('data-id', c.id);
      row.querySelector('[data-c-author]').textContent = name;
      row.querySelector('[data-c-avatar]').textContent = initialOf(name);
      row.querySelector('[data-c-text]').textContent = c.body;
      var time = row.querySelector('[data-c-time]');
      time.textContent = formatRelative(c.createdAt, this.gw.now ? this.gw.now() : c.createdAt);
      time.setAttribute('datetime', c.createdAt);
      return row;
    }

    onSubmit(event) {
      var form = event.target.closest('[data-comment-form]');
      if (!form) return;
      event.preventDefault();
      var self = this;
      var field = form.querySelector('nc-input');
      var input = form.querySelector('.nc-input__control');
      var button = form.querySelector('.nc-button');
      if (this.perms.gated) { this.gate('comment'); return; }
      if (button.getAttribute('data-state') === 'loading') return;
      button.setAttribute('data-state', 'loading');
      this.gw.addComment({ lookId: this.lookId, body: input.value }).then(function (c) {
        button.removeAttribute('data-state');
        var st = self.commentState;
        var list = self.q('[data-comment-list]');
        list.insertBefore(self.commentRow(c), list.firstChild);
        st.loaded += 1;
        st.total += 1;
        input.value = '';
        if (field.updateCounter) field.updateCounter();
        field.clearError();
        self.afterComments();
        self.announce(self.q('[data-comments-title]').textContent);
      }, function (err) {
        button.removeAttribute('data-state');
        if (err && err.kind === 'validation' && err.fieldErrors && err.fieldErrors.body) { field.setError(err.fieldErrors.body); input.focus(); return; }
        if (err && err.kind === 'unauthenticated') { self.gate('comment'); return; }
        self.toast('error', self.texts.feedback[2]);
      });
    }

    // comments sit inline from 600 px and in a bottom sheet below (PROPOSED): one DOM node moves between the two places
    placeComments() {
      if (!this.look) return;
      var node = this.q('[data-comments]');
      var slot = this.q('[data-comments-slot]');
      var sheet = this.q('[data-comments-sheet]');
      var body = this.q('[data-sheet-body]');
      var open = this.q('[data-comments-open]');
      if (this.mqMobile.matches) {
        if (node.parentNode !== body) body.appendChild(node);
        slot.hidden = true;
        open.hidden = false;
      } else {
        if (sheet.isOpen) sheet.close();
        if (node.parentNode !== slot) slot.appendChild(node);
        slot.hidden = false;
        open.hidden = true;
      }
    }

    openCommentsSheet(trigger) { this.q('[data-comments-sheet]').open(trigger); }
  }

  customElements.define('nc-look-detail', NcLookDetail);
})();
